import { Theme, CSSObject, styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import { alpha, keyframes } from "@mui/material";

import { Link as MuiLink, Button as MuiButton } from "@mzc-pdc/ui";
import theme from "@styles/theme";

const drawerWidth = 256;

/**
 * GNB 라벨은 최대 2줄까지 줄바꿈하고 2줄을 넘길 때만 말줄임 처리한다.
 * (pt-BR 등 EN 대비 문자열이 긴 로케일에서 1줄 말줄임으로 의미가 손실되는 문제 대응 - DAM-306)
 */
const NAV_LABEL_MAX_LINES = 2;

const navLabelClampStyle = {
    display: `-webkit-box`,
    WebkitBoxOrient: `vertical`,
    WebkitLineClamp: NAV_LABEL_MAX_LINES,
    overflow: `hidden`,
    whiteSpace: `normal`,
    wordBreak: `break-word`,
    textAlign: `left`,
};

export const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    // padding: `12px`,
    overflowX: "hidden",
});

export const closedMixin = (theme) => ({
    transition: theme.transitions.create("width ", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    width: `64px`,
    overflowX: "hidden",
});

export const ProjectSettingButton = styled(MuiButton)(({ theme }) => ({
    display: `inline-flex`,
    width: `-webkit-fill-available`,
    // 디자인시스템 theme의 .MuiButton-sizeMedium(padding 7.75px 20px, height 40)이
    // styled 값을 덮어써, 오른쪽 화살표(.MuiBox-root, position: absolute right 12px)와
    // 라벨이 겹치고 라벨이 2줄로 늘어나지도 못했다. && 로 우선순위를 높여 확실히 오버라이드한다.
    // - padding-right 40px: 화살표 폭(right 12px + 아이콘 22px)만큼 공간을 예약 → 라벨이 화살표 앞에서 줄바꿈
    // - height auto + minHeight 40px: 긴 라벨(pt-BR 등)이 최대 2줄까지 늘어나도록 (DAM-306)
    "&&": {
        padding: `8px 40px 8px 16px`,
        height: `auto`,
        minHeight: `40px`,
    },
    justifyContent: `flex-start`,
    alignItems: `center`,
    boxSizing: `border-box`,
    borderRadius: `3px`,
    fontWeight: 500,
    backgroundColor: theme.palette.greySecondary[50],
    margin: "0 12px 12px 12px",
    "> div": {
        display: `inline-flex`,
        alignItems: `center`,
    },
    ".MuiButton-startIcon": {
        marginLeft: 0,
    },
    ".nav-button-label": navLabelClampStyle,
    ".MuiBox-root": {
        display: `inline-flex`,
        alignItems: `center`,
        justifyContent: `center`,
        position: `absolute`,
        right: "12px",
        top: `50%`,
        transform: `translateY(-50%)`,
        transition: `all .2s linear`,
        ".MuiSvgIcon-root": {
            color: theme.palette.greySecondary[500],
            margin: `0 auto`,
        },
    },
    "&:hover": {
        backgroundColor: `${alpha(theme.palette.primary[100], 0.7)} !important`,
        ".MuiSvgIcon-root": {
            color: theme.palette.primary.main,
        },
        ".MuiBox-root": {
            right: 6,
        },
    },
}));

export const BackToConsoleButton = styled(MuiButton)(({ theme }) => ({
    display: `inline-flex`,
    minWidth: `40px`,
    minHeight: `40px`,
    width: `-webkit-fill-available`,
    padding: `8px 6px 8px 53px`,
    alignItems: `center`,
    justifyContent: `flex-start`,
    borderRadius: `3px`,
    fontWeight: 500,
    backgroundColor: theme.palette.greySecondary[50],
    margin: "0 12px 12px 12px",
    "> div": {
        display: `inline-flex`,
        alignItems: `center`,
    },
    ".nav-button-label": navLabelClampStyle,
    ".MuiBox-root": {
        display: `inline-flex`,
        alignItems: `center`,
        justifyContent: `center`,
        position: `absolute`,
        left: `15px`,
        top: `50%`,
        transform: `translateY(-50%)`,
        transition: `all .2s linear`,
        minWidth: `22px`,
        backgroundColor: theme.palette.secondary[800],
        ".MuiSvgIcon-root": {
            margin: `0 auto`,
        },
    },
    "&:hover": {
        backgroundColor: `${alpha(theme.palette.primary[100], 0.7)} !important`,
        ".MuiSvgIcon-root": {
            color: theme.palette.primary.main,
        },
        ".MuiBox-root": {
            left: `6px`,
            backgroundColor: theme.palette.primary[300],
            ".MuiSvgIcon-root": {
                color: theme.palette.primary.contrastText,
            },
        },
    },
}));

export const BackToProjectButton = styled(MuiButton)(({ theme }) => ({
    position: `relative`,
    display: `inline-flex`,
    minWidth: `40px`,
    minHeight: `40px`,
    padding: `8px 6px 8px 53px`,
    alignItems: `center`,
    boxSizing: `border-box`,
    justifyContent: `flex-start`,
    borderRadius: `3px`,
    fontWeight: 500,
    color: theme.palette.primary.contrastText,
    backgroundColor: alpha(theme.palette.greySecondary[50], 0.12),
    margin: "0 12px 12px 12px",
    "> div": {
        display: `inline-flex`,
        alignItems: `center`,
    },
    ".nav-button-label": navLabelClampStyle,
    ".MuiBox-root": {
        display: `inline-flex`,
        alignItems: `center`,
        justifyContent: `center`,
        position: `absolute`,
        left: `15px`,
        top: `50%`,
        transform: `translateY(-50%)`,
        transition: `all .2s linear`,
        minWidth: `22px`,
        backgroundColor: theme.palette.secondary[800],
        ".MuiSvgIcon-root": {
            margin: `0 auto`,
        },
    },
    "&:hover": {
        backgroundColor: `${alpha(theme.palette.greySecondary[50], 0.2)} !important`,
        ".MuiSvgIcon-root": {
            color: theme.palette.primary.main,
        },
        ".MuiBox-root": {
            left: `6px`,
            backgroundColor: theme.palette.primary[300],
            ".MuiSvgIcon-root": {
                color: theme.palette.primary.contrastText,
            },
        },
    },
}));

const toleft = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const toright = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
export const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== ["open", "type"],
})(({ theme, open, type }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "& .MuiPaper-root ": {
        boxSizing: "border-box",
        backgroundColor: `#f2f3f6`, //theme.palette.greySecondary[50],
        borderRight: `1px solid ${theme.palette.greySecondary[100]}`,
        zIndex: theme.zIndex.appBar - 100,
        ...(type === "administration" && {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
        }),
    },
    "& .MuiList-root": {
        flex: 1,
        padding: 0,
        overflowY: `hidden`,
        overflowX: `hidden`,
        scrollbarGutter: `stable `,
        "&:hover": {
            overflowY: `overlay`,
        },
        "&::-webkit-scrollbar": {
            width: 6,
            height: 12,
        },
        "&::-webkit-scrollbar-track": {
            background: `transparent`,
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: type === "administration" ? `#696b6c` : alpha(theme.palette.greySecondary.main, 0.16),
            borderRadius: 8,
            backgroundClip: `padding-box`,
            border: 0,
        },
        ".MuiListItem-root": {
            width: `auto`,
            marginRight: -6,
            padding: `4px 12px`,
            display: `block`,
            ".MuiList-root": {
                marginTop: 8,
                overflow: `visible`,
                ".MuiListItem-root": {
                    padding: 0,
                    marginRight: 0,
                    ".MuiListItemButton-root": {
                        "&.Mui-selected": {
                            ".MuiListItemIcon-root ": {
                                color:
                                    type === "administration"
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.primary.main,
                            },
                            "&:hover": {
                                backgroundColor:
                                    type === "administration"
                                        ? alpha(theme.palette.primary[300], 0.7)
                                        : alpha(theme.palette.primary[100], 0.3),
                            },
                        },
                    },
                },
            },
            "&.MuiListItem-divider": {
                border: 0,
                "&:after": {
                    content: "''",
                    display: `block`,
                    width: `100%`,
                    height: 1,
                    margin: `12px 0 6px`,
                    backgroundColor:
                        type === "administration" ? alpha(theme.palette.divider, 0.12) : theme.palette.divider,
                },
                "&:last-of-type": {
                    "&:after": {
                        content: `none`,
                    },
                },
            },
            ".MuiListItemButton-root": {
                minWidth: 40,
                minHeight: 40,
                height: `auto`,
                borderRadius: 3,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 12,
                paddingRight: 12,
                ".MuiListItemIcon-root ": {
                    minWidth: 16,
                    alignItems: `center`,
                    justifyContent: `center`,
                    marginRight: 12,
                    color:
                        type === "administration" ? theme.palette.secondary.contrastText : theme.palette.secondary.main,
                    ".MuiSvgIcon-root": {
                        fontSize: 16,
                    },
                },
                // 기본(비선택) 상태 아이콘 색 통일.
                // 대부분의 아이콘은 svg 에 color="#3c3d4f" 를 달고 나오지만, 일부(워크플로우 / AI Workspace)는
                // 그 속성이 없어 아이콘 패키지 기본값(#1B2856)으로 렌더돼 메뉴마다 색이 달랐다.
                // svg 를 직접 겨냥해 지정하면 프레젠테이션 속성보다 우선하므로 전부 같은 색이 된다.
                ".MuiListItemIcon-root svg": {
                    color:
                        type === "administration" ? theme.palette.secondary.contrastText : theme.palette.text.primary,
                },
                ".MuiListItemText-root": {
                    overflow: `hidden`,
                    ".MuiTypography-root": navLabelClampStyle,
                },
                "&:hover": {
                    backgroundColor:
                        type === "administration"
                            ? alpha(theme.palette.greySecondary[50], 0.12)
                            : theme.palette.greySecondary[50],
                },
                "&.Mui-selected": {
                    backgroundColor:
                        type === "administration"
                            ? alpha(theme.palette.primary[300], 0.5)
                            : alpha(theme.palette.primary[100], 0.5),
                    ".MuiListItemIcon-root ": {
                        color:
                            type === "administration" ? theme.palette.primary.contrastText : theme.palette.primary.main,
                    },
                    // @mzc-cloudplex/icons 아이콘은 svg 에 color="#3c3d4f"(gray-secondary/main) 프레젠테이션 속성을
                    // 직접 달고 나온다. 프레젠테이션 속성은 "상속받은 color" 보다 우선하므로, 위의
                    // .MuiListItemIcon-root 색이 선택 상태에서 아이콘에 반영되지 않는다.
                    // svg 를 직접 겨냥해 색을 지정하면(선택자 우선순위가 프레젠테이션 속성보다 높다) 해결된다.
                    ".MuiListItemIcon-root svg": {
                        color:
                            type === "administration" ? theme.palette.primary.contrastText : theme.palette.primary.main,
                    },
                    "&:hover": {
                        backgroundColor:
                            type === "administration"
                                ? alpha(theme.palette.primary[300], 0.7)
                                : alpha(theme.palette.primary[100], 0.7),
                    },
                },
            },
        },
        ".MuiCollapse-root": {
            ".MuiList-root": {
                marginTop: 8,
                overflow: `visible`,
                ".MuiListItem-root": {
                    padding: 0,
                    marginRight: 0,
                    ".MuiListItemButton-root": {
                        ".MuiListItemIcon-root ": {
                            opacity: 0.2,
                            ".MuiSvgIcon-root": {
                                fontSize: 6,
                            },
                        },
                        "&.Mui-selected": {
                            backgroundColor: `transparent`,
                            ".MuiListItemIcon-root ": {
                                opacity: 1,
                                color: theme.palette.primary.main,
                            },
                            "&:hover": {
                                backgroundColor:
                                    type === "administration"
                                        ? alpha(theme.palette.primary[300], 0.5)
                                        : alpha(theme.palette.primary[100], 0.3),
                            },
                        },
                    },
                },
            },
        },
        ".MuiListSubheader-root": {
            position: `static`,
            display: `flex`,
            alignItems: `center`,
            minHeight: 16,
            marginBottom: 8,
            paddingLeft: 14,
            backgroundColor: `transparent`,
            lineHeight: `initial`,
            // fontSize: theme.typography.body4.fontSize,
            fontWeight: `bold`,
            color: type === "administration" ? theme.palette.secondary[400] : theme.palette.text.secondary,
        },
    },

    "& .nav-toggle-button": {
        position: `fixed`,
        left: drawerWidth,
        bottom: `71px`,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: `26px`,
        height: `26px`,
        boxShadow: `0 3px 3px 0 rgba(0, 0, 0, 0.12)`,
        transition: theme.transitions.create("left ", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
        // "& .MuiListItemButton-root": {
        //     justifyContent: "initial",
        // },
        // "& .MuiListItemIcon-root": {
        //     marginRight: `10px`,
        // },
        // "& .MuiListItemText-root": {
        //     opacity: 1,
        // },
        "& .project-setting-button ": {
            strong: {
                opacity: 1,
            },
        },
        "& .back-to-console-button ": {
            strong: {
                opacity: 1,
            },
        },
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
        // 접힌 상태(64px)에서는 라벨이 보이지 않으므로 2줄 줄바꿈이 필요 없다.
        // 행 높이가 늘어나면 아이콘 정렬이 깨지므로 펼친 상태와 동일 특이도로 1줄 말줄임을 복원한다.
        "& .MuiList-root .MuiListItem-root .MuiListItemButton-root": {
            height: 40,
            paddingTop: 0,
            paddingBottom: 0,
            ".MuiListItemText-root .MuiTypography-root": {
                display: `block`,
                WebkitLineClamp: `unset`,
                whiteSpace: `nowrap`,
                textOverflow: `ellipsis`,
            },
        },
        // "& .MuiListItemButton-root": {
        //     justifyContent: "center",
        //     overflow: `hidden`,
        // },
        // "& .MuiListItemIcon-root": {
        //     marginRight: `0`,
        // },
        // "& .MuiListItemText-root": {
        //     opacity: 0,
        //     width: 0,
        // },
        "& .project-setting-button ": {
            minWidth: 40,
            width: 40,
            // margin: `0 auto 12px`,
            padding: 12,
            "span.css-0": {
                width: 0,
                opacity: 0,
                margin: 0,
                overflow: `hidden`,
            },
            ".MuiBox-root": {
                width: 0,
                opacity: 0,
            },
        },
        "& .back-to-console-button ": {
            width: `40px`,
            minWidth: `40px`,
            padding: `9px`,
            // margin: `0 auto 12px`,
            "span.css-0": {
                width: 0,
                opacity: 0,
                margin: 0,
            },
            ".MuiBox-root": {
                left: `9px`,
            },
        },
        "& .nav-toggle-button": {
            position: `fixed`,
            left: `60px`,
            bottom: `71px`,
            width: `26px`,
            height: `26px`,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: `${theme.palette.secondary[500]} !important`,
            transition: theme.transitions.create("left ", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        },
    }),
}));
